import { fetchAPI } from './fetchAPI';

// ==================== Analytics ====================

export const compareVersions = async (params: Record<string, any>): Promise<any> => {
  return fetchAPI('/genaiops/analytics/compare-versions', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export const getAnomalies = async (lookbackDays: number = 7, thresholdStd: number = 2, severity: string | null = null): Promise<any> => {
  const queryParams = new URLSearchParams();
  queryParams.append('lookback_days', String(lookbackDays));
  queryParams.append('threshold_std', String(thresholdStd));
  if (severity) queryParams.append('severity', severity);

  return fetchAPI(`/genaiops/analytics/anomalies?${queryParams.toString()}`);
};

export const getCostForecast = async (daysAhead: number = 30, lookbackDays: number = 30): Promise<any> => {
  const queryParams = new URLSearchParams();
  queryParams.append('days_ahead', String(daysAhead));
  queryParams.append('lookback_days', String(lookbackDays));

  return fetchAPI(`/genaiops/analytics/cost-forecast?${queryParams.toString()}`);
};

export const getCostBreakdown = async (options: Record<string, any> = {}, legacyGroupBy?: string, legacyClientId?: string): Promise<any> => {
  // Support legacy call signature: getCostBreakdown(days, groupBy, clientId)
  let days, groupBy, startDate, endDate, clientId;
  if (typeof options === 'number') {
    days = options;
    groupBy = legacyGroupBy || 'agent';
    clientId = legacyClientId;
  } else {
    days = options.days;
    groupBy = options.groupBy || 'agent';
    startDate = options.start_date;
    endDate = options.end_date;
    clientId = options.clientId;
  }

  const queryParams = new URLSearchParams();
  // Only add date params if provided - when all are null/undefined,
  // the backend returns all data ("All Time" filter)
  if (startDate && endDate) {
    queryParams.append('start_date', startDate);
    queryParams.append('end_date', endDate);
  } else if (days) {
    queryParams.append('days', String(days));
  }
  // Note: When no date params are provided, backend returns all data
  queryParams.append('group_by', groupBy);
  if (clientId) {
    queryParams.append('client_id', clientId);
  }

  return fetchAPI(`/genaiops/analytics/cost-breakdown?${queryParams.toString()}`);
};

export const getRealtimeMetrics = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.days) queryParams.append('days', params.days);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/analytics/realtime${query ? '?' + query : ''}`);
};

// ==================== Approval Workflow ====================

export const getApprovals = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.resource_type) queryParams.append('resource_type', params.resource_type);
  if (params.to_environment) queryParams.append('to_environment', params.to_environment);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/approvals${query ? '?' + query : ''}`);
};

export const getApprovalSummary = async (): Promise<any> => {
  return fetchAPI('/genaiops/approvals/summary');
};

export const getApproval = async (requestId: string): Promise<any> => {
  return fetchAPI(`/genaiops/approvals/${requestId}`);
};

export const createApproval = async (data: Record<string, unknown>): Promise<any> => {
  return fetchAPI('/genaiops/approvals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const approveApproval = async (requestId: string, data: Record<string, unknown>): Promise<any> => {
  return fetchAPI(`/genaiops/approvals/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const rejectApproval = async (requestId: string, data: Record<string, unknown>): Promise<any> => {
  return fetchAPI(`/genaiops/approvals/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const cancelApproval = async (requestId: string, userId: string): Promise<any> => {
  return fetchAPI(`/genaiops/approvals/${requestId}/cancel?user_id=${userId}`, {
    method: 'POST',
  });
};

export const getReleaseHistory = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.prompt_id) queryParams.append('prompt_id', params.prompt_id);
  if (params.prompt_name) queryParams.append('prompt_name', params.prompt_name);
  if (params.environment) queryParams.append('environment', params.environment);
  if (params.released_by) queryParams.append('released_by', params.released_by);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/approvals/releases/history${query ? '?' + query : ''}`);
};

export const getAuditReport = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.resource_type) queryParams.append('resource_type', params.resource_type);
  if (params.action) queryParams.append('action', params.action);
  if (params.user_id) queryParams.append('user_id', params.user_id);
  if (params.date_from) queryParams.append('date_from', params.date_from);
  if (params.date_to) queryParams.append('date_to', params.date_to);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.format) queryParams.append('format', params.format);

  const query = queryParams.toString();
  return fetchAPI(`/genaiops/approvals/audit/report${query ? '?' + query : ''}`);
};

// ==================== APM (Application Performance Monitoring) ====================

export const getAPMMetrics = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.agentName) queryParams.append('agent_name', params.agentName);
  if (params.runtime) queryParams.append('runtime', params.runtime);
  if (params.runId) queryParams.append('run_id', params.runId);
  if (params.projectId) queryParams.append('project_id', params.projectId);
  if (params.timeRange) queryParams.append('time_range', params.timeRange);

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/metrics${query ? '?' + query : ''}`);
};

export const getAPMMetricsSummary = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.timeRange) queryParams.append('time_range', params.timeRange);
  if (params.agentName) queryParams.append('agent_name', params.agentName);
  if (params.projectId) queryParams.append('project_id', params.projectId);

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/metrics/summary${query ? '?' + query : ''}`);
};

export const getAPMMetricsTimeseries = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.metric) queryParams.append('metric', params.metric);
  if (params.interval) queryParams.append('interval', params.interval);
  if (params.timeRange) queryParams.append('time_range', params.timeRange);
  if (params.agentName) queryParams.append('agent_name', params.agentName);
  if (params.projectId) queryParams.append('project_id', params.projectId);

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/metrics/timeseries${query ? '?' + query : ''}`);
};

export const getAPMHealth = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.agentName) queryParams.append('agent_name', params.agentName);
  if (params.timeRange) queryParams.append('time_range', params.timeRange);
  if (params.time_range) queryParams.append('time_range', params.time_range);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.client_id) queryParams.append('client_id', params.client_id);
  if (params.projectId) queryParams.append('project_id', params.projectId);
  if (params.project_id) queryParams.append('project_id', params.project_id);

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/health${query ? '?' + query : ''}`);
};

export const getMemoryLeakDetection = async (agentName: string, windowHours: number = 24, projectId?: string | null): Promise<any> => {
  const queryParams = new URLSearchParams();
  queryParams.append('agent_name', agentName);
  queryParams.append('window_hours', String(windowHours));
  if (projectId) queryParams.append('project_id', projectId);

  return fetchAPI(`/agentops/apm/health/memory-leak-detection?${queryParams}`);
};

export const getAPMAlerts = async (params: Record<string, any> = {}): Promise<any> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.status) queryParams.append('status', params.status);
  if (params.severity) queryParams.append('severity', params.severity);
  if (params.agentName) queryParams.append('agent_name', params.agentName);

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/alerts${query ? '?' + query : ''}`);
};

export const acknowledgeAPMAlert = async (alertId: string, acknowledgedBy: string): Promise<any> => {
  return fetchAPI(`/agentops/apm/alerts/${alertId}/acknowledge?acknowledged_by=${acknowledgedBy}`, {
    method: 'POST',
  });
};

export const resolveAPMAlert = async (alertId: string): Promise<any> => {
  return fetchAPI(`/agentops/apm/alerts/${alertId}/resolve`, {
    method: 'POST',
  });
};

export const getAPMAlertRules = async (enabledOnly = false) => {
  const queryParams = new URLSearchParams();
  if (enabledOnly) queryParams.append('enabled_only', 'true');

  const query = queryParams.toString();
  return fetchAPI(`/agentops/apm/alert-rules${query ? '?' + query : ''}`);
};

export const createAPMAlertRule = async (rule: Record<string, any>): Promise<any> => {
  return fetchAPI('/agentops/apm/alert-rules', {
    method: 'POST',
    body: JSON.stringify(rule),
  });
};

export const updateAPMAlertRule = async (ruleId: string, updates: Record<string, any>): Promise<any> => {
  return fetchAPI(`/agentops/apm/alert-rules/${ruleId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

export const deleteAPMAlertRule = async (ruleId: string): Promise<any> => {
  return fetchAPI(`/agentops/apm/alert-rules/${ruleId}`, {
    method: 'DELETE',
  });
};

export const seedDefaultAPMRules = async () => {
  return fetchAPI('/agentops/apm/alert-rules/seed-defaults', {
    method: 'POST',
  });
};

export const bulkDeleteVectorExports = async (ids: string[]): Promise<any> => {
  return fetchAPI('/genaiops/vector-exports/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
};
